;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { utils = {}, api = {}, analytics = {} } = namespace
  const { escapeHtml = (v) => (v == null ? '' : String(v)), encodeKey = (v) => v } = utils
  const { searchStylists = async () => [] } = api

  const AUTOCOMPLETE_MIN_LENGTH = 2
  const AUTOCOMPLETE_LIMIT = 3
  const VALID_STYLIST_ROLES = new Set(['Elite Artist', 'Master Technician', 'Certified Stylist'])

  // ============================================================================
  // Search Input Setup
  // ============================================================================

  /**
   * Setup search inputs with autocomplete functionality.
   */
  function setupSearchInputs(
    searchInputs,
    searchContainers,
    autocompleteDropdowns,
    autocompleteContents,
    searchButtons,
    state,
    callbacks
  ) {
    const { onSearchChange = () => {}, onPlaceSelect = () => {}, onStylistSelect = () => {}, onGeocode = () => {} } =
      callbacks

    // Close dropdown on outside click
    document.addEventListener('click', (e) => {
      if (state.isDropdownOpen) {
        const clickedInside = searchContainers.some((c) => c.contains(e.target))
        if (!clickedInside) hideAutocompleteDropdown(autocompleteDropdowns, state)
      }
    })

    searchInputs.forEach((input, index) => {
      const dropdown = autocompleteDropdowns[index]
      const content = autocompleteContents[index]
      const container = searchContainers[index]

      input.addEventListener('input', (e) => {
        const value = e.target.value.trim()
        state.search = value
        syncSearchInputs(value, input, searchInputs)
        onSearchChange(value)

        if (value.length >= AUTOCOMPLETE_MIN_LENGTH) {
          handleAutocompleteSearch(value, content, container, state, callbacks)
        } else {
          cancelPendingSearch(state)
          content.innerHTML = ''
          hideAutocompleteDropdown([dropdown], state)
        }
      })

      input.addEventListener('focus', () => {
        if (state.search?.length >= AUTOCOMPLETE_MIN_LENGTH) {
          handleAutocompleteSearch(state.search, content, container, state, callbacks)
        }
      })

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          cancelPendingSearch(state)

          const isVisible = dropdown.style.display !== 'none' && state.isDropdownOpen
          const activeResult = content.querySelector('[data-autocomplete-item].is-active')

          if (isVisible && activeResult) {
            activeResult.click()
          } else {
            hideAutocompleteDropdown([dropdown], state)
            onGeocode()
          }
        } else if (e.key === 'Escape') {
          hideAutocompleteDropdown([dropdown], state)
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          navigateAutocomplete('down', content)
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          navigateAutocomplete('up', content)
        }
      })

      input.addEventListener('blur', () => {
        setTimeout(() => {
          if (!container.contains(document.activeElement)) {
            hideAutocompleteDropdown([dropdown], state)
          }
        }, 200)
      })
    })

    searchButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        hideAutocompleteDropdown(autocompleteDropdowns, state)
        onGeocode()
      })
    })
  }

  function syncSearchInputs(value, origin, searchInputs) {
    searchInputs.forEach((input) => {
      if (input !== origin) input.value = value
    })
  }

  function cancelPendingSearch(state) {
    if (state.autocompleteTimeout) {
      clearTimeout(state.autocompleteTimeout)
      state.autocompleteTimeout = null
    }
    if (state.pendingAutocompleteRequest) {
      state.pendingAutocompleteRequest.cancel?.()
      state.pendingAutocompleteRequest = null
    }
  }

  // ============================================================================
  // Autocomplete
  // ============================================================================

  async function handleAutocompleteSearch(query, contentElement, container, state, callbacks) {
    cancelPendingSearch(state)
    state.currentQuery = query

    state.autocompleteTimeout = setTimeout(() => {
      performAutocompleteSearch(query, contentElement, container, state, callbacks)
    }, 150)
  }

  async function performAutocompleteSearch(query, contentElement, container, state, callbacks) {
    if (state.currentQuery !== query) return

    const locations = []
    const stylists = []
    const requestId = Date.now()
    let stylistsAbortController = null

    state.pendingAutocompleteRequest = { id: requestId, cancel: () => {} }

    const shouldRender = () =>
      state.currentQuery === query && state.pendingAutocompleteRequest?.id === requestId

    const renderDropdown = () => {
      if (shouldRender()) {
        renderAutocompleteDropdown(locations, stylists, contentElement, container, query, state, callbacks)
      }
    }

    // Search places
    if (state.autocompleteService && globalThis.google) {
      const request = { input: query, types: ['geocode'] }
      const center = state.referenceCenter || state.searchCenter || state.userCenter

      if (center && state.map) {
        const bounds = state.map.getBounds()
        if (bounds) {
          request.bounds = bounds
        } else {
          request.location = new globalThis.google.maps.LatLng(center.lat, center.lng)
          request.radius = 50000
        }
      }

      state.autocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (!shouldRender()) return
        if (status === globalThis.google.maps.places.PlacesServiceStatus.OK && predictions) {
          locations.push(...predictions.slice(0, AUTOCOMPLETE_LIMIT))
        }
        renderDropdown()
      })
    }

    // Search stylists
    const apiEndpoint = state.apiEndpoint || ''
    stylistsAbortController = new AbortController()
    state.pendingAutocompleteRequest.cancel = () => stylistsAbortController?.abort()

    try {
      const results = await searchStylists(
        apiEndpoint,
        query,
        state.cachedStylists || [],
        stylistsAbortController.signal
      )
      if (!shouldRender()) return
      stylists.push(...results)
      renderDropdown()
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('Error searching stylists:', error)
      if (shouldRender() && locations.length > 0) renderDropdown()
    }
  }

  // ============================================================================
  // Dropdown Rendering
  // ============================================================================

  function renderAutocompleteDropdown(locations, stylists, contentElement, container, query, state, callbacks) {
    const { onPlaceSelect = () => {}, onStylistSelect = () => {} } = callbacks

    if (locations.length === 0 && stylists.length === 0) {
      const dropdown = getAutocompleteDropdown(container)
      if (dropdown) setDropdownOpen(dropdown, state, false)
      return
    }

    const htmlParts = []

    if (locations.length > 0) {
      htmlParts.push(renderAutocompleteSection('Locations', locations, (place) =>
        renderLocationItem(place, query)
      ))
    }

    if (stylists.length > 0) {
      htmlParts.push(renderAutocompleteSection('Stylists', stylists, (stylist) =>
        renderStylistItem(stylist, query)
      ))
    }

    contentElement.innerHTML = htmlParts.join('')
    showAutocompleteDropdown(container, state)

    // Track stylists appearing in search results
    if (stylists.length > 0 && analytics.trackSearchAppearances) {
      analytics.trackSearchAppearances(stylists, query)
    }

    contentElement.querySelectorAll('[data-autocomplete-item]').forEach((item) => {
      item.addEventListener('click', () => {
        if (item.dataset.type === 'location') {
          onPlaceSelect(item.dataset.placeId)
        } else if (item.dataset.type === 'stylist') {
          const emailKey = item.dataset.stylistEmail
          const stylist = stylists.find((s) => encodeKey(s.email) === emailKey)
          onStylistSelect(emailKey, stylist)
        }
      })
    })
  }

  function highlightMatch(text, query) {
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<span class="stylist-locator__autocomplete-highlight">$1</span>')
  }

  function renderAutocompleteSection(title, items, renderer) {
    return `
      <div class="stylist-locator__autocomplete-section">
        <div class="stylist-locator__autocomplete-section-title">${title}</div>
        <div class="stylist-locator__autocomplete-section-items">
          ${items.map(renderer).join('')}
        </div>
      </div>
    `
  }

  function renderLocationItem(place, query) {
    const mainText = escapeHtml(place.structured_formatting?.main_text || place.description)
    const secondary = escapeHtml(place.structured_formatting?.secondary_text || '')
    return `
      <div class="stylist-locator__autocomplete-item" data-autocomplete-item data-place-id="${escapeHtml(place.place_id)}" data-type="location">
        <div class="stylist-locator__autocomplete-item-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="21" viewBox="0 0 16 21" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.26075 0.114868C7.60604 -0.0188656 7.98332 -0.0363002 8.33875 0.0650512L8.48925 0.114868L14.6143 2.49249C14.9248 2.61304 15.1959 2.82246 15.3959 3.09636C15.5959 3.37026 15.7166 3.69736 15.7439 4.03953L15.75 4.18898V9.04657C15.75 10.5143 15.3671 11.9548 14.6419 13.216C13.9167 14.4772 12.876 15.5123 11.6296 16.212L11.3969 16.3379L8.46212 17.8569C8.30053 17.9404 8.12403 17.9885 7.94366 17.9982C7.76329 18.0078 7.58293 17.9789 7.41387 17.9131L7.28788 17.8569L4.35313 16.3379C3.08491 15.6815 2.01143 14.6828 1.246 13.4473C0.480579 12.2118 0.051608 10.7852 0.0043751 9.3183L0 9.04657V4.18898C5.08817e-06 3.84581 0.0941733 3.50971 0.271542 3.21979C0.448911 2.92987 0.702183 2.69807 1.00187 2.55136L1.13575 2.49249L7.26075 0.114868ZM10.8789 5.94253L6.85563 10.1072L5.30863 8.50583C5.14444 8.336 4.9218 8.24063 4.68969 8.24072C4.45758 8.2408 4.23501 8.33633 4.07094 8.50629C3.90687 8.67625 3.81474 8.90671 3.81482 9.14698C3.8149 9.38725 3.90719 9.61765 4.07137 9.78749L6.17488 11.9649C6.26426 12.0575 6.37038 12.1309 6.48719 12.181C6.60399 12.2311 6.72919 12.2569 6.85563 12.2569C6.98206 12.2569 7.10726 12.2311 7.22406 12.181C7.34087 12.1309 7.44699 12.0575 7.53638 11.9649L12.1161 7.22328C12.1997 7.13972 12.2664 7.03978 12.3122 6.92927C12.3581 6.81877 12.3822 6.69991 12.3832 6.57965C12.3842 6.45938 12.3621 6.34011 12.3181 6.2288C12.2741 6.11748 12.2091 6.01635 12.127 5.93131C12.0448 5.84626 11.9471 5.77901 11.8396 5.73346C11.7321 5.68792 11.6168 5.665 11.5006 5.66605C11.3845 5.66709 11.2696 5.69208 11.1629 5.73955C11.0561 5.78702 10.9596 5.85602 10.8789 5.94253Z" fill="black"/>
          </svg>
        </div>
        <div class="stylist-locator__autocomplete-item-text">
          <span class="stylist-locator__autocomplete-item-main">${highlightMatch(mainText, query)}</span>
          ${secondary ? `<span class="stylist-locator__autocomplete-item-secondary">${secondary}</span>` : ''}
        </div>
      </div>
    `
  }

  function renderStylistItem(stylist, query) {
    const name = escapeHtml(stylist.name || 'Unnamed Stylist')
    const rawRole = (stylist.role || '').trim()
    const isSimpleSalonOwner = stylist.isSalonOwner === true && !VALID_STYLIST_ROLES.has(rawRole)
    const roleText = isSimpleSalonOwner ? 'Certified Salon' : rawRole || 'IBE® Stylist'
    const role = escapeHtml(roleText)
    return `
      <div class="stylist-locator__autocomplete-item" data-autocomplete-item data-stylist-email="${encodeKey(stylist.email)}" data-type="stylist">
        <div class="stylist-locator__autocomplete-item-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" fill="black"/>
            <path d="M8 9C5.23858 9 3 11.2386 3 14H13C13 11.2386 10.7614 9 8 9Z" fill="black"/>
          </svg>
        </div>
        <div class="stylist-locator__autocomplete-item-text">
          <span class="stylist-locator__autocomplete-item-main">${highlightMatch(name, query)}</span>
          <span class="stylist-locator__autocomplete-item-secondary">${role}</span>
        </div>
      </div>
    `
  }

  function getAutocompleteDropdown(container) {
    return container
      .closest('[data-element="search-container"]')
      ?.querySelector('[data-element="autocomplete-dropdown"]')
  }

  function setDropdownOpen(dropdown, state, isOpen) {
    if (!dropdown) return
    dropdown.style.display = isOpen ? 'block' : 'none'
    state.isDropdownOpen = isOpen
  }

  function showAutocompleteDropdown(container, state) {
    const dropdown = getAutocompleteDropdown(container)
    setDropdownOpen(dropdown, state, true)
  }

  function hideAutocompleteDropdown(dropdowns, state) {
    dropdowns.forEach((d) => {
      setDropdownOpen(d, state, false)
    })
  }

  function navigateAutocomplete(direction, contentElement) {
    const items = Array.from(contentElement.querySelectorAll('[data-autocomplete-item]'))
    if (items.length === 0) return

    const currentIndex = items.findIndex((item) => item.classList.contains('is-active'))
    let newIndex

    if (direction === 'down') {
      newIndex = currentIndex === -1 ? 0 : currentIndex < items.length - 1 ? currentIndex + 1 : 0
    } else {
      newIndex = currentIndex === -1 ? items.length - 1 : currentIndex > 0 ? currentIndex - 1 : items.length - 1
    }

    items.forEach((item, i) => item.classList.toggle('is-active', i === newIndex))
    items[newIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }

  // ============================================================================
  // Place Selection
  // ============================================================================

  function handlePlaceSelection(placeId, placesService, callbacks) {
    if (!placesService || !globalThis.google) return

    const { onPlaceSelected = () => {} } = callbacks

    placesService.getDetails(
      { placeId, fields: ['geometry', 'formatted_address', 'name'] },
      (place, status) => {
        if (status === globalThis.google.maps.places.PlacesServiceStatus.OK && place) {
          const location = place.geometry.location
            onPlaceSelected(
              { lat: location.lat(), lng: location.lng() },
              place.formatted_address || place.name
            )
        }
      }
    )
  }

  // ============================================================================
  // Geocoding
  // ============================================================================

  function triggerGeocode(searchQuery, geocoder, callbacks) {
    if (!geocoder || !searchQuery) return

    const { onGeocodeSuccess = () => {}, onGeocodeError = () => {} } = callbacks

    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const location = results[0].geometry.location
        onGeocodeSuccess({ lat: location.lat(), lng: location.lng() })
      } else {
        onGeocodeError()
      }
    })
  }

  // ============================================================================
  // Exports
  // ============================================================================

  namespace.search = Object.assign({}, namespace.search, {
    setupSearchInputs,
    syncSearchInputs,
    handlePlaceSelection,
    triggerGeocode,
    hideAutocompleteDropdown,
  })
})()
